import { Button, Typography } from '@material-ui/core';
import BackupIcon from '@material-ui/icons/Backup';
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile';
import InsertPhotoIcon from '@material-ui/icons/InsertPhoto';
import { useSnackbar } from 'notistack';
import React from 'react';
import { FileRejection, useDropzone } from 'react-dropzone';
import useStyles from './style';
import { IDropzoneProps, IStyleProps } from './types';
import {
  converFileToImage,
  validateFileImageByResolution,
  validateMaxFilesByType,
} from './utils';

const LOCALE = {
  'file-too-large': 'Arquivo muito grande',
  'too-many-files': 'Muitos arquivos',
  'file-too-small': 'Arquivo muito pequeno',
  'file-invalid-type': 'Tipo de arquivo inválido',
};

function Dropzone({
  files,
  setFiles,
  accept,
  maxFiles,
  maxFilesByType,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  maxResolution,
  ...restProps
}: IDropzoneProps) {
  const { enqueueSnackbar } = useSnackbar();

  const isDisabled = React.useMemo(
    () => (maxFiles ? files.length >= maxFiles : false),
    [files.length, maxFiles],
  );

  const classes = useStyles({ isDisabled } as IStyleProps);

  const suportedFormats = React.useMemo<string[]>(() => {
    function getSuportedFormats(formats: string[]) {
      return formats.map(format => {
        const str = format.split('/');
        return str.length < 2 ? str[0] : str[1];
      });
    }

    if (Array.isArray(accept)) {
      return getSuportedFormats(accept);
    }

    return getSuportedFormats(accept.split(','));
  }, [accept]);

  const setAcceptedFiles = React.useCallback(
    (_files: File[]) => {
      setFiles(currentFiles => {
        const arrOfFiles = [...currentFiles, ..._files];
        const filteredFiles: File[] = [];
        const objMap = {};

        arrOfFiles.forEach(file => {
          if (!objMap[file.name]) {
            objMap[file.name] = true;
            filteredFiles.push(file);
          }
        });
        return filteredFiles;
      });
    },
    [setFiles],
  );

  const setFileRejectionsErrorMessages = React.useCallback(
    (fileRejections: FileRejection[]) => {
      const messages: string[] = Array.from(
        new Set(
          fileRejections.map(fileRejected => {
            return `${fileRejected.file.name}: ${
              fileRejected.errors.slice(0, 1).map(error => {
                return LOCALE[error.code] || error.message || '';
              })[0]
            }`;
          }),
        ),
      );

      messages.forEach(msg =>
        enqueueSnackbar(msg, {
          variant: 'error',
        }),
      );
    },
    [enqueueSnackbar],
  );

  const deleteFileByIndex = React.useCallback(
    (selectedIndex: number) => {
      setFiles(currentFiles => {
        const copyFiles = [...currentFiles];

        const index =
          typeof currentFiles[selectedIndex] === 'undefined'
            ? -1
            : selectedIndex;
        if (index > -1) {
          copyFiles.splice(index, 1);
        }

        return copyFiles;
      });
    },
    [setFiles],
  );

  const renderFiles = React.useCallback(() => {
    if (files.length === 0) return null;
    return files.map((file, index) => {
      const Icon = file.type.includes('image')
        ? InsertPhotoIcon
        : InsertDriveFileIcon;
      return (
        <div className={classes.files} key={file.name}>
          <div className={classes.filesText}>
            <Icon className={classes.fileIcon} />
            <h4>{file.name}</h4>
          </div>

          <Button onClick={() => deleteFileByIndex(index)} color="primary">
            Excluir
          </Button>
        </div>
      );
    });
  }, [
    classes.fileIcon,
    classes.files,
    classes.filesText,
    deleteFileByIndex,
    files,
  ]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept,
    getFilesFromEvent: converFileToImage,
    validator: (file: File) => {
      if (maxResolution) {
        const validateImage = validateFileImageByResolution(
          file,
          maxResolution,
        );
        if (validateImage) {
          return validateImage;
        }
      }

      return null;
    },
    onDrop: (_acceptedFiles, _rejectedFiles) => {
      if (maxFilesByType) {
        const validateFilesByType = validateMaxFilesByType(
          [..._acceptedFiles, ...files],
          maxFilesByType,
        );

        if (validateFilesByType) {
          // Reject everything and empty accepted files
          _acceptedFiles.forEach(file => {
            _rejectedFiles.push({ file, errors: [validateFilesByType] });
          });
          _acceptedFiles.splice(0);
        }

        setAcceptedFiles(_acceptedFiles);
        setFileRejectionsErrorMessages(_rejectedFiles);
      }
    },
    maxFiles,
    disabled: isDisabled,
    ...restProps,
  });

  return (
    <section>
      <div {...getRootProps({ className: classes.dropzone })}>
        <input {...getInputProps()} />
        <BackupIcon className={classes.backupIcon} />
        <Typography className={classes.chooseFile}>Escolher Arquivo</Typography>
        {!isDragActive && (
          <Typography className={classes.dragHere}>ou anexe aqui</Typography>
        )}
      </div>
      <p className={classes.format}>
        {`Formatos suportados: ${suportedFormats.join(', ')}`}
      </p>

      <aside>{renderFiles()}</aside>
    </section>
  );
}

export default Dropzone;
